import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Link as MuiLink, styled, Typography } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import {
  BottomActionButton,
  ButtonsColumnContainer,
  FormBody,
  FormContentContainer,
  FormHeader,
  SecondaryActionButton,
} from "@components";
import { Routes, onContactUsClick } from "@utils";
import { failedClaveUnicaValidationWsp } from "@constants/index";

const StyledErrorIcon = styled(ErrorOutline)(({ theme }) => ({
  fontSize: "46px",
  color: theme.palette.error.main,
}));

const Link = styled(MuiLink)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.link,
  cursor: "pointer",
}));

interface Props {
  description?: string | React.ReactNode;
  onRetry: () => void;
  // Only where the failed step used the saved ID serial: a typo in it is one
  // cause the doctor can fix, and retrying would send the same number again.
  onCorrectCiDocNum?: () => void;
}

export const ClaveUnicaRetryScreen: React.FC<Props> = ({
  description,
  onRetry,
  onCorrectCiDocNum,
}) => {
  const navigate = useNavigate();

  const handleGoHome = useCallback(() => {
    navigate(Routes.SOLICITUDES);
  }, [navigate]);

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer>
        <FormBody justifyContent="center">
          <Box
            marginBottom="48px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="16px"
          >
            <StyledErrorIcon />
            <Typography variant="h5" fontWeight="semibold" textAlign="center">
              Error al validar identidad con clave única
            </Typography>
            {description ? (
              <Typography
                variant="subtitle1"
                component="div"
                textAlign="center"
              >
                {description}
              </Typography>
            ) : null}
            <Typography variant="subtitle1" component="div" textAlign="center">
              Si el problema persiste,{" "}
              <Link
                onClick={() => onContactUsClick(failedClaveUnicaValidationWsp)}
              >
                contáctanos
              </Link>{" "}
              para ayudarte.
            </Typography>
          </Box>
          <ButtonsColumnContainer>
            <BottomActionButton onClick={onRetry}>Reintentar</BottomActionButton>
            {onCorrectCiDocNum ? (
              <SecondaryActionButton useBackground onClick={onCorrectCiDocNum}>
                Corregir número de documento
              </SecondaryActionButton>
            ) : null}
            <SecondaryActionButton onClick={handleGoHome}>
              Volver al inicio
            </SecondaryActionButton>
          </ButtonsColumnContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
